import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Award,
  BookOpen,
  Briefcase,
  Star,
  Heart,
  Building2,
  Sparkles,
  GraduationCap,
  ScrollText,
  Phone
} from 'lucide-react';

const AboutPage = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-20">

      <div className="max-w-7xl mx-auto">

        {/* Hero Section */}
        <div className="text-center mb-20">
          <h1 className="text-4xl md:text-6xl font-serif text-[#F5F2EB] mb-4">
            {isHindi
              ? 'सुरेंद्र कुमार पटवारी'
              : 'About Surendra Kumar Patawari'}
          </h1>

          <p className="text-[#C9A84C] text-lg tracking-wider uppercase">
            {isHindi
              ? 'वैदिक ज्योतिष एवं वास्तु विशेषज्ञ'
              : 'Vedic Astrology & Vastu Consultant'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Left Side */}
          <div className="lg:col-span-4">

            <div className="bg-[#111111] border border-[#C9A84C]/20 rounded-3xl p-8 sticky top-24">

              <img
                src="/images/Surendra.jpeg"
                alt="Surendra Kumar Patawari"
                className="w-72 h-72 rounded-full object-cover mx-auto border-4 border-[#C9A84C]/20"
              />

              <h2 className="text-3xl font-serif text-[#F5F2EB] text-center mt-8">
                {isHindi
                  ? 'सुरेंद्र कुमार पटवारी'
                  : 'Surendra Kumar Patawari'}                 
                
              </h2>

              <p className="text-[#C9A84C] text-center mt-2">
                {isHindi
                  ? 'ज्योतिषी एवं वास्तु सलाहकार'
                  : 'Astrologer & Vastu Consultant'}
              </p>

              <div className="mt-8 space-y-4">

                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <Briefcase className="text-[#C9A84C]" size={18} />
                  <span>
                    {isHindi
                      ? '17+ वर्षों का अनुभव'
                      : '17+ Years of Experience'}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <Award className="text-[#C9A84C]" size={18} />
                  <span>
                    {isHindi
                      ? 'ज्योतिष कोविद सम्मान'
                      : 'Jyotish Kovid Award'}
                  </span>
                </div>

          

                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <Star className="text-[#C9A84C]" size={18} />
                  <span>
                    {isHindi
                      ? '5000+ सफल परामर्श'
                      : '5000+ Successful Consultations'}
                  </span>
                </div>

              </div>

            </div>

          </div>

          {/* Right Side */}
          <div className="lg:col-span-8 space-y-10">

            {/* Introduction */}
            <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

              <h3 className="text-3xl font-serif text-[#F5F2EB] mb-6">
                {isHindi ? 'परिचय' : 'Introduction'}
              </h3>

              <div className="space-y-5 text-[#A3A3A3] leading-relaxed text-lg">

                <p>
                  {isHindi
                    ? 'सुरेंद्र कुमार पटवारी हैदराबाद के एक प्रतिष्ठित ज्योतिषी, वास्तु सलाहकार, शिक्षक और शोधकर्ता हैं। उन्हें वैदिक ज्योतिष, वास्तु शास्त्र, जैन दर्शन, बुद्ध दर्शन तथा आध्यात्मिक विज्ञानों में गहरी रुचि है।'
                    : 'Surendra Kumar Patawari is a highly respected astrologer, vastu consultant, teacher, and researcher based in Hyderabad with over 17+ years of experience in the fields of Vedic Astrology, Vastu Shastra, Jain Philosophy, Buddha Philosophy, and spiritual sciences.'}
                </p>

                <p>
                  {isHindi
                    ? 'उन्होंने वर्ष 2008 में जैन विश्व भारती संस्थान से ज्योतिष की शिक्षा “A” ग्रेड के साथ पूर्ण की। वर्ष 2009 में उन्होंने इंडियन काउंसिल ऑफ एस्ट्रोलॉजिकल साइंसेज (ICAS) से प्रतिष्ठित ज्योतिष विशारद की उपाधि प्राप्त की।'
                    : 'He completed his astrology studies with “A” Grade from Jain Vishva Bharati Institute in 2008. In 2009, he earned the prestigious Jyotish Visharad qualification from the Indian Council of Astrological Sciences.'}
                </p>

                <p>
                  {isHindi
                    ? 'वर्ष 2014 में ज्योतिष के क्षेत्र में उनके उत्कृष्ट योगदान के लिए उन्हें “ज्योतिष कोविद” सम्मान से सम्मानित किया गया। वर्तमान में वे ICAS हैदराबाद के प्रतिष्ठित सदस्य हैं और ज्योतिष एवं वास्तु विज्ञान में अध्यापन, मार्गदर्शन एवं परामर्श कार्य कर रहे हैं।'
                    : 'In recognition of his contribution to astrology, he was honored with the title “Jyotish Kovid” by ICAS in 2014. Currently, he is a reputed member of ICAS Hyderabad and is actively involved in teaching, mentoring, and consulting in astrology and vastu sciences.'}
                </p>

              </div>

            </div>

            {/* Specialization */}
            <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

              <h3 className="text-3xl font-serif text-[#F5F2EB] mb-6">
                {isHindi ? 'विशेषज्ञता' : 'Specialization'}
              </h3>

              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-6">
                {isHindi
                  ? 'इनकी विशेषज्ञता एडवांस कम्पोजिट तकनीक, प्रेडिक्टिव ज्योतिष, विवाह समय विश्लेषण एवं व्यावहारिक कुंडली अध्ययन में है।'
                  : 'His specialization lies in Advanced Composite Techniques, predictive astrology, marriage timing analysis, and practical horoscope interpretation.'}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {[
                  {
                    icon: Heart,
                    title: isHindi
                      ? 'विवाह एवं संबंध मार्गदर्शन'
                      : 'Marriage & Relationship Guidance'
                  },
                  {
                    icon: Briefcase,
                    title: isHindi
                      ? 'करियर एवं व्यवसाय ज्योतिष'
                      : 'Career & Business Astrology'
                  },
                  {
                    icon: Building2,
                    title: isHindi
                      ? 'घर एवं ऑफिस वास्तु परामर्श'
                      : 'Vastu Consultation for Home & Office'
                  },
                  {
                    icon: Sparkles,
                    title: isHindi
                      ? 'स्वास्थ्य एवं वित्तीय ज्योतिष'
                      : 'Health & Financial Astrology'
                  },
                  {
                    icon: ScrollText,
                    title: isHindi
                      ? 'मुहूर्त चयन'
                      : 'Muhurat Selection'
                  },
                  {
                    icon: BookOpen,
                    title: isHindi
                      ? 'कुंडली मिलान एवं विवाह समय'
                      : 'Kundli Matching & Marriage Timing'
                  },
                  {
                    icon: Star,
                    title: isHindi
                      ? 'आध्यात्मिक एवं जीवन मार्गदर्शन'
                      : 'Spiritual Counseling & Life Guidance'
                  },
                  {
                    icon: Award,
                    title: isHindi
                      ? 'जैन आध्यात्मिक ज्ञान एवं मंत्र साधना'
                      : 'Jain Spiritual Wisdom & Mantra Practices'
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-[#1A1A1A] rounded-2xl p-5 flex items-start gap-4 border border-[#C9A84C]/5"
                  >
                    <item.icon className="text-[#C9A84C]" size={22} />

                    <span className="text-[#D4D4D4]">
                      {item.title}
                    </span>
                  </div>
                ))}

              </div>

            </div>

            {/* Philosophy */}
            <div className="bg-gradient-to-r from-[#1A1A1A] to-[#111111] border border-[#C9A84C]/20 rounded-3xl p-8">

              <h3 className="text-3xl font-serif text-[#F5F2EB] mb-6">
                {isHindi ? 'जीवन दर्शन' : 'Philosophy'}
              </h3>

              <p className="text-[#D4D4D4] text-lg leading-relaxed italic">
                {isHindi
                  ? '“ज्योतिष केवल भविष्यवाणी नहीं है, बल्कि स्पष्टता, सही समय, निर्णय क्षमता और आत्मिक विकास का एक शक्तिशाली माध्यम है।”'
                  : '"Astrology is not merely prediction, but a powerful tool for clarity, timing, decision-making, and spiritual growth."'}
              </p>

              <p className="text-[#A3A3A3] mt-6 leading-relaxed">
                {isHindi
                  ? 'उनकी परामर्श शैली पारंपरिक वैदिक सिद्धांतों और व्यावहारिक जीवन अनुभवों का सुंदर संयोजन है।'
                  : 'His consultation approach combines traditional Vedic principles with practical real-life applications.'}
              </p>

            </div>

            {/* Key Highlights */}
            <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

              <h3 className="text-3xl font-serif text-[#F5F2EB] mb-8">
                {isHindi ? 'मुख्य विशेषताएँ' : 'Key Highlights'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {[
                  isHindi
                    ? '17+ वर्षों का अनुभव'
                    : '17+ Years of Experience in Astrology & Vastu Consultation',

                  isHindi
                    ? 'ICAS हैदराबाद में शिक्षक एवं मार्गदर्शक'
                    : 'Astrology Teacher & Mentor at ICAS Hyderabad',

                  isHindi
                    ? 'एडवांस कम्पोजिट एवं प्रेडिक्टिव तकनीकों में विशेषज्ञ'
                    : 'Specialized in Advanced Composite & Predictive Techniques',

                  isHindi
                    ? 'प्रमाणित ज्योतिष विशारद एवं ज्योतिष कोविद'
                    : 'Certified Jyotish Visharad & Jyotish Kovid',

                  isHindi
                    ? 'विवाह एवं संबंध ज्योतिष में विशेषज्ञता'
                    : 'Expertise in Marriage Timing & Relationship Astrology',

                  isHindi
                    ? 'जैन एवं बुद्ध दर्शन का गहरा ज्ञान'
                    : 'Deep Knowledge of Jain Philosophy & Buddha Philosophy',

                  isHindi
                    ? 'शोध आधारित परामर्श दृष्टिकोण'
                    : 'Practical, Research-Based Consultation Approach',

                  isHindi
                    ? 'व्यक्तिगत एवं व्यवसायिक मार्गदर्शन'
                    : 'Personalized Guidance for Individuals, Families & Businesses',
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 bg-[#1A1A1A] rounded-2xl p-5"
                  >
                    <span className="text-[#C9A84C] text-xl">✦</span>

                    <span className="text-[#D4D4D4]">
                      {item}
                    </span>
                  </div>
                ))}

              </div>

            </div>

            {/* Author Section */}
            <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

              <h3 className="text-3xl font-serif text-[#F5F2EB] mb-6">
                {isHindi ? 'लेखक एवं शोध कार्य' : 'Author & Research Work'}
              </h3>

              <p className="text-[#A3A3A3] text-lg leading-relaxed">
                {isHindi
                  ? 'ज्योतिषीय सिद्धांतों के व्यावहारिक उपयोग एवं विवाह समय निर्धारण को समझने के लिए पाठक उनकी पुस्तक “ज्योतिष द्वारा सही समय विवाह का” का अध्ययन कर सकते हैं, जो उनके शोध आधारित एवं अनुभवपूर्ण दृष्टिकोण को दर्शाती है।'
                  : 'To understand the practical application of astrological principles for marriage timing and life events, readers can explore his work “Jyotish Dwara Sahi Samay Vivah Ka”, which reflects his research-oriented and experience-driven approach toward astrology.'}
              </p>

            </div>

      

            {/* CTA */}
            <div className="text-center bg-gradient-to-r from-[#C9A84C]/10 to-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-3xl p-10">

              <h3 className="text-3xl font-serif text-[#F5F2EB] mb-4">
                {isHindi
                  ? 'आज ही परामर्श बुक करें'
                  : 'Book Your Consultation Today'}
              </h3>

              <p className="text-[#A3A3A3] mb-8">
                {isHindi
                  ? 'जीवन में सही दिशा, सही समय और सकारात्मक ऊर्जा के लिए विशेषज्ञ मार्गदर्शन प्राप्त करें।'
                  : 'Get expert guidance for clarity, right timing, positive energy, and spiritual growth.'}
              </p>

              <button className="bg-[#C9A84C] hover:bg-[#d6b45d] transition-all duration-300 text-black px-8 py-4 rounded-full font-semibold flex items-center gap-3 mx-auto">
                <Phone size={18} />
                {isHindi
                  ? 'अपॉइंटमेंट बुक करें'
                  : 'Book Appointment'}
              </button>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default AboutPage;